import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ChevronDown, 
  ChevronUp, 
  ShieldAlert,
  User,
  AlertTriangle,
  AlertCircle,
  Info,
  Eye,
  X,
  Loader2,
  CheckCircle,
  BookmarkPlus
} from 'lucide-react';

import { personHref } from '../utils/prettyArchiveLinks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Severity badge styling
const SeverityBadge = ({ severity }) => {
  const styles = {
    error: 'bg-red-900/30 text-red-400 border-red-800/50',
    warning: 'bg-amber-900/30 text-amber-400 border-amber-800/50',
    info: 'bg-blue-900/30 text-blue-400 border-blue-800/50'
  };
  
  const labels = {
    error: 'Error',
    warning: 'Warning',
    info: 'Review'
  };
  
  const icons = {
    error: <AlertCircle className="w-3 h-3" />,
    warning: <AlertTriangle className="w-3 h-3" />,
    info: <Info className="w-3 h-3" />
  };
  
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full border uppercase tracking-[0.1em] font-medium ${styles[severity] || styles.info}`}>
      {icons[severity]}
      {labels[severity] || 'Info'}
    </span>
  );
};

// Issue type icons
const IssueIcon = ({ type }) => {
  const iconClass = "w-4 h-4";
  
  switch (type) {
    case 'parent_younger_than_child':
    case 'impossible_lifespan_overlap':
      return <AlertCircle className={`${iconClass} text-red-500`} />;
    case 'generational_gap_too_small':
    case 'generational_gap_too_large':
      return <AlertTriangle className={`${iconClass} text-amber-500`} />;
    case 'spouse_loop':
    case 'sibling_gap_too_large':
      return <Info className={`${iconClass} text-blue-500`} />;
    default:
      return <ShieldAlert className={`${iconClass} text-amber-500`} />;
  }
};

// Single Validation Issue Card
const ValidationIssueCard = ({ issue, treeId, treeName, onDismiss, onSaveToJournal, isProcessing, isSavingToJournal }) => {
  const navigate = useNavigate();
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  
  const handleReview = () => {
    // Navigate to the first affected person's profile with highlight param
    if (issue.affected_people?.[0]?.id) {
      // Include issue context in URL params for highlighting
      const params = new URLSearchParams();
      params.set('highlight', 'relationship');
      params.set('issue_type', issue.issue_type);
      if (issue.affected_people.length > 1) {
        params.set('related_person', issue.affected_people[1].id);
      }
      navigate(`${personHref(issue.affected_people[0])}?${params.toString()}`);
      
      // Show toast to guide user
      toast.info(`Reviewing ${issue.affected_people[0].name}'s relationships`, { duration: 3000 });
    }
  };
  
  const handleConfirmDismiss = () => {
    onDismiss(issue.id);
    setShowDismissDialog(false);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="bg-card border-secondary hover:border-amber-900/40 transition-all duration-300">
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-start gap-2">
              <div className="mt-0.5 p-1.5 rounded-lg bg-secondary border border-border">
                <IssueIcon type={issue.issue_type} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground/80 leading-relaxed">
                  {issue.description}
                </p>
              </div>
            </div>
            <SeverityBadge severity={issue.severity} />
          </div>
          
          {/* Affected People */}
          {issue.affected_people && issue.affected_people.length > 0 && (
            <div className="ml-9 mb-3">
              <div className="flex flex-wrap gap-1.5">
                {issue.affected_people.map((person, idx) => (
                  <span 
                    key={idx}
                    className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-secondary text-foreground border border-border"
                  >
                    <User className="w-2.5 h-2.5" />
                    {person.name}
                    <span className="text-muted-foreground">({person.role})</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Suggested Fix */}
          <div className="ml-9 mb-3 p-2 rounded-lg bg-secondary border border-border">
            <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider mb-1">Suggested correction</p>
            <p className="text-xs text-muted-foreground/80">{issue.suggested_fix}</p>
          </div>
          
          {/* Action buttons */}
          <div className="ml-9 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReview}
              disabled={isProcessing}
              className="flex-1 h-7 text-xs border-border text-foreground hover:bg-secondary"
              data-testid={`review-btn-${issue.id}`}
            >
              <Eye className="w-3 h-3 mr-1.5" />
              Review Relationship
            </Button>
            {onSaveToJournal && treeId && treeName && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSaveToJournal(issue)}
                disabled={isProcessing || isSavingToJournal}
                className="h-7 px-2 text-[10px] border-border text-muted-foreground hover:text-foreground"
                data-testid={`save-validation-to-journal-${issue.id}`}
              >
                {isSavingToJournal ? <Loader2 className="w-3 h-3 animate-spin" /> : <BookmarkPlus className="w-3 h-3 mr-1" />}
                Add to Journal
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDismissDialog(true)}
              disabled={isProcessing}
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary"
              data-testid={`ignore-btn-${issue.id}`}
              title="Ignore this warning"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Dismiss confirmation dialog */}
      <Dialog open={showDismissDialog} onOpenChange={setShowDismissDialog}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Ignore This Warning?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              You're about to dismiss this validation warning. The relationship will remain unchanged.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <div className="p-3 rounded-lg bg-secondary border border-border">
              <div className="flex items-start gap-2">
                <IssueIcon type={issue.issue_type} />
                <div className="flex-1">
                  <p className="text-sm text-foreground/90 mb-1">{issue.description}</p>
                  <SeverityBadge severity={issue.severity} />
                </div>
              </div>
            </div>
            
            {/* Affected people */}
            {issue.affected_people && issue.affected_people.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Involved family members</p>
                <div className="flex flex-wrap gap-1.5">
                  {issue.affected_people.map((person, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-secondary text-foreground border border-border"
                    >
                      <User className="w-2.5 h-2.5" />
                      {person.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <p className="text-[11px] text-muted-foreground/70 italic">
              Dismissing this warning means you've reviewed it and believe the data is correct, 
              or you'll address it later.
            </p>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDismissDialog(false)}
              className="border-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDismiss}
              disabled={isProcessing}
              className="bg-stone-700 hover:bg-muted-foreground"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              Dismiss Warning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

// Main Relationship Validation Panel
const RelationshipValidationPanel = ({ treeId, treeName }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [savingToJournalId, setSavingToJournalId] = useState(null);
  const [issuesBySeverity, setIssuesBySeverity] = useState({ error: 0, warning: 0, info: 0 });

  const handleSaveToJournal = async (issue) => {
    if (!treeId || !treeName) return;
    setSavingToJournalId(issue.id);
    try {
      await axios.post(`${API}/investigations`, {
        tree_id: treeId,
        tree_name: treeName,
        insight_id: issue.id,
        insight_type: 'validation',
        insight_title: issue.description || 'Relationship validation concern',
        insight_description: issue.suggested_fix || issue.description || '',
        search_params: { issue_type: issue.issue_type, severity: issue.severity },
        linked_individual_ids: (issue.affected_people || []).map((p) => p.id).filter(Boolean),
        notes: null
      });
      toast.success('Validation concern added to Research Journal');
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already saved')) {
        toast.info('This concern is already in your Research Journal');
      } else {
        toast.error(err.response?.data?.detail || 'Could not add to journal');
      }
    } finally {
      setSavingToJournalId(null);
    }
  };
  
  const fetchValidation = async () => {
    if (!treeId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/trees/${treeId}/validation`);
      setIssues(response.data.issues || []);
      setIssuesBySeverity(response.data.issues_by_severity || { error: 0, warning: 0, info: 0 });
    } catch (err) {
      console.error('Failed to fetch validation:', err);
      setError('Unable to validate relationships');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchValidation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when treeId changes only
  }, [treeId]);
  
  const handleDismiss = async (issueId) => {
    setIsProcessing(true);
    
    try {
      await axios.post(`${API}/trees/${treeId}/validation/${issueId}/dismiss`);
      
      toast.success('Warning dismissed', { duration: 2000 });
      
      // Remove from local list
      setIssues(prev => {
        const newIssues = prev.filter(i => i.id !== issueId);
        // Recalculate counts
        const newCounts = { error: 0, warning: 0, info: 0 };
        newIssues.forEach(i => {
          if (i.severity in newCounts) newCounts[i.severity]++;
        });
        setIssuesBySeverity(newCounts);
        return newIssues;
      });
    } catch (err) {
      console.error('Dismiss failed:', err);
      toast.error('Failed to dismiss warning');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Show success state if no issues
  if (!loading && issues.length === 0) {
    return (
      <div className="w-full" data-testid="relationship-validation-panel">
        <Card className="bg-gradient-to-br from-card to-stone-950/80 border-secondary shadow-lg">
          <CardContent className="py-4 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-900/20 border border-emerald-800/30">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground/90">All relationships verified</p>
                <p className="text-[10px] text-muted-foreground/60">No validation issues detected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const totalErrors = issuesBySeverity.error || 0;
  const totalWarnings = issuesBySeverity.warning || 0;
  
  return (
    <div className="w-full" data-testid="relationship-validation-panel">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Card className="bg-gradient-to-br from-card to-stone-950/80 border-secondary shadow-lg">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-secondary transition-colors py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded-lg border ${totalErrors > 0 ? 'bg-red-900/30 border-red-800/40' : 'bg-amber-900/30 border-amber-800/40'}`}>
                    <ShieldAlert className={`w-4 h-4 ${totalErrors > 0 ? 'text-red-500' : 'text-amber-500'}`} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground/90">
                      Relationship Validation
                    </h3>
                    <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                      {totalErrors > 0 ? `${totalErrors} error${totalErrors > 1 ? 's' : ''} found` : 
                       totalWarnings > 0 ? `${totalWarnings} warning${totalWarnings > 1 ? 's' : ''} to review` :
                       'Issues to review'}
                    </p>
                  </div>
                  {issues.length > 0 && (
                    <span className={`ml-2 px-2 py-0.5 text-[10px] font-medium rounded-full border ${
                      totalErrors > 0 
                        ? 'bg-red-900/30 text-red-400 border-red-800/40'
                        : 'bg-amber-900/30 text-amber-400 border-amber-800/40'
                    }`}>
                      {issues.length}
                    </span>
                  )}
                </div>
                {isOpen ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground/60" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground/60" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <CardContent className="pt-0 px-4 pb-4">
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 text-amber-500/60 animate-spin" />
                  <span className="ml-2 text-xs text-muted-foreground">Validating relationships...</span>
                </div>
              ) : error ? (
                <div className="text-center py-4">
                  <p className="text-xs text-muted-foreground/70">{error}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-[11px] text-muted-foreground/70 italic">
                    These relationships may require verification.
                  </p>
                  
                  <AnimatePresence mode="popLayout">
                    {issues.map((issue, index) => (
                      <motion.div
                        key={issue.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <ValidationIssueCard 
                          issue={issue} 
                          treeId={treeId}
                          treeName={treeName}
                          onDismiss={handleDismiss}
                          onSaveToJournal={handleSaveToJournal}
                          isProcessing={isProcessing}
                          isSavingToJournal={savingToJournalId === issue.id}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  
                  {/* Footer note */}
                  <p className="text-[10px] text-muted-foreground/50 text-center pt-2 italic">
                    Review and correct as needed
                  </p>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export default RelationshipValidationPanel;
